/**
 * Radar - Top-down mini-map showing positions of all entities.
 * Uses Babylon GUI (AdvancedDynamicTexture fullscreen).
 */

import * as GUI from "babylonjs-gui";
import * as BABYLON from "babylonjs";

const RADAR_W = 160;
const RADAR_H = 100;
const HALF_FIELD_X = 20;
const HALF_FIELD_Z = 30;
const MAX_DEFENDERS = 3;
const MAX_GOALKEEPERS = 2;

export class Radar {
  private _texture: GUI.AdvancedDynamicTexture;
  private _container: GUI.Rectangle;
  private _playerDot: GUI.Ellipse;
  private _ballDot: GUI.Ellipse;
  private _defenderDots: GUI.Ellipse[] = [];
  private _goalkeeperDots: GUI.Ellipse[] = [];

  constructor() {
    this._texture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("radar");

    // Container
    this._container = new GUI.Rectangle("radarContainer");
    this._container.width = `${RADAR_W}px`;
    this._container.height = `${RADAR_H}px`;
    this._container.cornerRadius = 2;
    this._container.color = "rgba(255,255,255,0.2)";
    this._container.thickness = 1;
    this._container.background = "rgba(0,0,0,0.6)";
    this._container.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
    this._container.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
    this._container.left = "-20px";
    this._container.top = "110px"; // Position it below the timer box
    this._texture.addControl(this._container);

    // Label
    const label = new GUI.TextBlock("radarLabel", "LIVE RADAR");
    label.color = "#aaaaaa";
    label.fontSize = 8;
    label.fontWeight = "700";
    label.fontFamily = "Orbitron, sans-serif";
    label.textVerticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
    label.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    label.top = "2px";
    this._container.addControl(label);

    // Player dot (neon blue)
    this._playerDot = this._createDot("playerDot", "#1155cc", 8);
    this._container.addControl(this._playerDot);

    // Ball dot (neon yellow)
    this._ballDot = this._createDot("ballDot", "#ffcc00", 6);
    this._container.addControl(this._ballDot);

    // Defender dots (neon red)
    for (let i = 0; i < MAX_DEFENDERS; i++) {
      const dot = this._createDot(`defDot${i}`, "#ff3366", 6);
      dot.isVisible = false;
      this._defenderDots.push(dot);
      this._container.addControl(dot);
    }

    // Goalkeeper dots (neon purple)
    for (let i = 0; i < MAX_GOALKEEPERS; i++) {
      const dot = this._createDot(`gkDot${i}`, "#aa44cc", 6);
      dot.isVisible = false;
      this._goalkeeperDots.push(dot);
      this._container.addControl(dot);
    }
  }

  private _createDot(
    name: string,
    color: string,
    size: number
  ): GUI.Ellipse {
    const dot = new GUI.Ellipse(name);
    dot.width = `${size}px`;
    dot.height = `${size}px`;
    dot.color = "white";
    dot.thickness = 1;
    dot.background = color;
    return dot;
  }

  private _worldToRadar(
    worldX: number,
    worldZ: number
  ): { x: number; y: number } {
    const x = (worldX / HALF_FIELD_X) * (RADAR_W / 2);
    const y = -(worldZ / HALF_FIELD_Z) * (RADAR_H / 2);
    return { x, y };
  }

  update(
    playerPos: BABYLON.Vector3,
    ballPos: BABYLON.Vector3,
    defenderPositions: BABYLON.Vector3[],
    goalkeeperPositions: BABYLON.Vector3[]
  ): void {
    // Player
    const p = this._worldToRadar(playerPos.x, playerPos.z);
    this._playerDot.left = `${p.x}px`;
    this._playerDot.top = `${p.y}px`;

    // Ball
    const b = this._worldToRadar(ballPos.x, ballPos.z);
    this._ballDot.left = `${b.x}px`;
    this._ballDot.top = `${b.y}px`;

    // Defenders
    for (let i = 0; i < MAX_DEFENDERS; i++) {
      if (i < defenderPositions.length) {
        const d = this._worldToRadar(
          defenderPositions[i].x,
          defenderPositions[i].z
        );
        this._defenderDots[i].left = `${d.x}px`;
        this._defenderDots[i].top = `${d.y}px`;
        this._defenderDots[i].isVisible = true;
      } else {
        this._defenderDots[i].isVisible = false;
      }
    }

    // Goalkeepers
    for (let i = 0; i < MAX_GOALKEEPERS; i++) {
      if (i < goalkeeperPositions.length) {
        const g = this._worldToRadar(
          goalkeeperPositions[i].x,
          goalkeeperPositions[i].z
        );
        this._goalkeeperDots[i].left = `${g.x}px`;
        this._goalkeeperDots[i].top = `${g.y}px`;
        this._goalkeeperDots[i].isVisible = true;
      } else {
        this._goalkeeperDots[i].isVisible = false;
      }
    }
  }

  setVisible(visible: boolean): void {
    this._container.isVisible = visible;
  }

  dispose(): void {
    this._texture.dispose();
  }
}
